import { Link } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';
import Reveal from '@/components/Reveal';

const HERO_IMAGE = '/images/proposal-59.jpg';
const HERO_HIGH_RES_IMAGE = '/images/proposal-59-3840.webp';
const STORY_IMAGE = '/images/proposal-9.jpg';
const LIBRARY_IMAGE = '/images/peabody-interior-2400.webp';

export default function HomePage() {
  const { showRsvp } = useSettings();

  return (
    <div data-testid="home-page">
      <section className="home-hero" aria-labelledby="home-hero-title">
        <picture>
          <source
            type="image/webp"
            srcSet={HERO_HIGH_RES_IMAGE}
          />
          <img
            src={HERO_IMAGE}
            alt="Lexi and James overlooking the water"
            className="home-hero__image"
            data-testid="hero-image"
            width="5935"
            height="3957"
            loading="eager"
            fetchPriority="high"
          />
        </picture>
        <div className="home-hero__wash" aria-hidden="true" />

        <div className="home-hero__content">
          <p className="eyebrow mb-5 text-linen/85 opacity-0 animate-fade-in">
            Baltimore · 2027
          </p>
          <h1
            id="home-hero-title"
            data-testid="hero-title"
            className="home-hero__title opacity-0 animate-fade-in delay-100"
          >
            Lexi <span className="home-hero__ampersand">&amp;</span> James
          </h1>

          {showRsvp && (
            <Link
              to="/rsvp"
              data-testid="hero-rsvp-btn"
              className="mt-8 inline-flex items-center justify-center min-h-12 px-7 border border-linen text-linen text-sm uppercase tracking-[0.14em] font-semibold hover:bg-linen hover:text-forest transition-colors"
            >
              RSVP
            </Link>
          )}
        </div>

        <div className="home-hero__details" data-testid="quick-facts">
          <span data-testid="hero-date">April 24, 2027</span>
          <span>George Peabody Library · Baltimore, Maryland</span>
        </div>
      </section>

      <section className="intro-band" aria-label="Wedding introduction">
        <div className="intro-band__grid">
          <p className="intro-band__date" aria-label="April 24, 2027">
            04.24.<br className="intro-band__date-break" />2027
          </p>
          <div className="intro-band__copy">
            <p className="eyebrow text-olive mb-3">Save the date</p>
            <p>
              We’re getting married in Baltimore, and we can’t wait to celebrate
              with all of our favorite people!
            </p>
          </div>
        </div>
      </section>

      <section className="home-story home-story--editorial">
        <div className="home-story__grid">
          <Reveal as="figure" className="home-story__photo">
            <img
              src={STORY_IMAGE}
              alt="Lexi and James walking together"
              data-testid="story-image"
              loading="lazy"
            />
          </Reveal>

          <Reveal className="home-story__card">
            <p className="eyebrow text-olive">The weekend</p>
            <h2>The Wedding</h2>
            <p>
              Join us for a welcome reception in Fells Point, followed by our
              ceremony and reception at the George Peabody Library.
            </p>
            <Link to="/schedule" data-testid="schedule-link" className="btn-outline">
              View the Schedule
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="venue-feature venue-feature--open" aria-labelledby="venue-heading">
        <img
          src={LIBRARY_IMAGE}
          srcSet="/images/peabody-interior-1200.webp 1200w, /images/peabody-interior-2400.webp 2400w"
          sizes="100vw"
          alt="The soaring interior of the George Peabody Library"
          className="venue-feature__image"
          data-testid="venue-image"
          loading="lazy"
        />
        <div className="venue-feature__wash" aria-hidden="true" />

        <Reveal className="venue-feature__card">
          <p className="eyebrow">Saturday, April 24</p>
          <h2 id="venue-heading">George Peabody Library</h2>
          <p>Ceremony, cocktails, dinner, and dancing to follow.</p>
        </Reveal>
        <span className="venue-feature__location">Mount Vernon · Baltimore</span>
      </section>
    </div>
  );
}
